/**
 * MUI Roof component that replaces the styled-components Roof component
 * 
 * This component provides a Material UI equivalent of the original Roof component
 * with similar props and functionality.
 */
import React from "react";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { getConditionResult } from "reduxModules/selectors/containerSelectors";
import { getBoundingBox } from "reduxModules/ducks/ui";
import { 
  MuiRoofProps, 
  RoofGroupProps, 
  RoofArrowProps,
  RoofConfigProps
} from "types/components/elements";
import { RootState } from "types/redux/state";

/**
 * MUI Roof component
 */
const MuiRoof: React.FC<MuiRoofProps> = (props) => {
  const bbox = useSelector(getBoundingBox);
  const roofType = useSelector((state: RootState) => state.container.roofType);
  const slopeOrientation = useSelector((state: RootState) => state.container.roofSlopeOrientation);
  const hor = /top|bottom|vertical/i.test(slopeOrientation);
  const start = /right|top|vertical|horizontal/i.test(slopeOrientation) ? 1 : 0;
  const end = /left|bottom|vertical|horizontal/i.test(slopeOrientation) ? 1 : 0;
  const slope = Array.from({ length: 3 }, (_, i) => (hor ? bbox.cx : bbox.cy) + (i - 1) * (hor ? bbox.width : bbox.height) * .4);

  const visible = useSelector((state: RootState) => getConditionResult(state, {...props.show}));
  const active = useSelector((state: RootState) => getConditionResult(state, {...props.active}));

  if (bbox.width * bbox.height > 0) {
    const roofProps: RoofConfigProps = {
      hor: hor,
      start: start,
      end: end,
      bBox: bbox
    };

    return (
      visible && (
        <RoofGroup id="roof" active={active}>
          {roofType === 'aframe' && (
            <RoofPath 
              d={`m${hor ? bbox.x0 : bbox.cx} ${hor ? bbox.cy : bbox.y0}h${hor ? bbox.width : 0}v${hor ? 0 : bbox.height}`}
            />
          )}
          {roofType !== 'flat' && slope.map((pos, i) => (
            <RoofArrow 
              key={`arr${i}`} 
              d={`${start ? 'm0 0 v-40 l5 15 c-5 -5 -5 -5 -10 0 l5 -15z' : ''} ${end ? 'm0 20 v40 l5 -15 c-5 5 -5 5 -10 0 l5 15z' : ''}`}
              transform={`
                translate(
                  ${hor ? pos : bbox.x1 - bbox.width * (start + (end ? 0 : 1)) * .5 + 10} 
                  ${hor ? bbox.y1 - bbox.height * ((start ? 0 : 1) + end) * .5 - 10 : pos}
                ) 
                rotate(${hor ? 0 : 90})
              `}
            />
          ))}
        </RoofGroup>
      )
    );
  }
  
  return null;
};

// Styled components using MUI styled API
const RoofGroup = styled('g', {
  shouldForwardProp: (prop) => prop !== 'active',
})<RoofGroupProps>`
  fill: none;
  stroke: ${({ active }) => active ? 'var(--app-red)' : 'var(--app-dark-gray)'};
  stroke-width: 2;
`;

const RoofPath = styled('path')<RoofArrowProps>`
  /* No additional styling needed */
`;

const RoofArrow = styled('path')<RoofArrowProps>`
  fill: var(--app-red);
  shape-rendering: geometricPrecision;
`;

export default MuiRoof;
